<template>
  <div class="plan-implementation">
    <template v-if="loading || !customer">
      <b-card class="loading-card">
        <div class="d-flex flex-column align-items-center justify-content-center py-4">
          <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem" />
          <span class="text-muted mt-1">Carregando plano de implementação...</span>
        </div>
      </b-card>
    </template>

    <template v-else>
      <!-- Hero -->
      <b-card no-body class="plan-hero mb-2">
        <div class="plan-hero__bg" />
        <b-card-body class="plan-hero__body">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="d-flex align-items-center plan-hero__identity">
              <b-avatar
                size="52"
                variant="light-primary"
                class="plan-hero__avatar"
                :text="initials"
              />
              <div class="ml-1">
                <div class="plan-hero__eyebrow">Tarefas Meu Futuro App</div>
                <h3 class="plan-hero__title mb-0">{{ customer.name }}</h3>
                <div class="plan-hero__meta">
                  <feather-icon icon="ActivityIcon" size="13" class="mr-25" />
                  <span>{{ statusLabel }}</span>
                </div>
              </div>
            </div>

            <div class="d-flex align-items-center plan-hero__actions">
              <div class="plan-progress">
                <svg viewBox="0 0 44 44" class="plan-progress__ring">
                  <circle class="plan-progress__track" cx="22" cy="22" r="19" />
                  <circle
                    class="plan-progress__value"
                    cx="22"
                    cy="22"
                    r="19"
                    :stroke-dasharray="ringCircumference"
                    :stroke-dashoffset="ringOffset"
                  />
                </svg>
                <div class="plan-progress__label">
                  <strong>{{ overallProgress }}%</strong>
                  <small>concluído</small>
                </div>
              </div>

              <b-button
                variant="outline-secondary"
                class="btn-icon ml-1"
                title="Voltar"
                @click="goBack"
              >
                <feather-icon icon="ArrowLeftIcon" />
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- KPIs -->
      <b-row class="match-height">
        <b-col
          v-for="stat in stats"
          :key="stat.key"
          cols="6"
          lg="3"
          class="mb-2"
        >
          <b-card no-body class="stat-card h-100">
            <b-card-body class="d-flex align-items-center">
              <div class="stat-card__icon" :class="`stat-card__icon--${stat.variant}`">
                <feather-icon :icon="stat.icon" size="20" />
              </div>
              <div class="ml-1">
                <h4 class="mb-0 stat-card__value">{{ stat.value }}</h4>
                <small class="text-muted">{{ stat.label }}</small>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Conteúdo -->
      <b-card no-body class="plan-tabs">
        <b-tabs
          v-model="activeTab"
          nav-class="plan-tabs__nav"
          content-class="plan-tabs__content"
        >
          <b-tab>
            <template #title>
              <feather-icon icon="FlagIcon" size="15" class="mr-50" />
              <span>Marcos</span>
              <b-badge pill variant="light-primary" class="ml-50">
                {{ milestones.length }}
              </b-badge>
            </template>
            <MilestoneList :token="customer.token" />
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon icon="CheckSquareIcon" size="15" class="mr-50" />
              <span>Tarefas</span>
              <b-badge pill variant="light-primary" class="ml-50">
                {{ tasks.length }}
              </b-badge>
            </template>
            <TaskList :token="customer.token" />
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon icon="TargetIcon" size="15" class="mr-50" />
              <span>Objetivos secundários</span>
              <b-badge pill variant="light-primary" class="ml-50">
                {{ secondaryGoals.length }}
              </b-badge>
            </template>
            <SecondaryGoalList :token="customer.token" />
          </b-tab>
        </b-tabs>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BSpinner,
  BCard,
  BCardBody,
  BAvatar,
  BButton,
  BBadge,
  BTabs,
  BTab,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import moment from "moment";
import * as sharedTypes from "@/modules/shared/store/types";
import * as types from "../store/types";
import TaskList from "../components/TaskList.vue";
import MilestoneList from "../components/MilestoneList.vue";
import SecondaryGoalList from "../components/SecondaryGoalList.vue";

const RING_RADIUS = 19;

export default {
  components: {
    BRow,
    BCol,
    BSpinner,
    BCard,
    BCardBody,
    BAvatar,
    BButton,
    BBadge,
    BTabs,
    BTab,
    TaskList,
    MilestoneList,
    SecondaryGoalList,
  },
  data() {
    return {
      loading: false,
      customer: undefined,
      activeTab: 1,
    };
  },
  setup() {
    return { toast: useToast() };
  },
  computed: {
    ...mapGetters({
      tasks: types.TASKS,
      milestones: types.MILESTONES,
      secondaryGoals: types.SECONDARY_GOALS,
    }),
    initials() {
      if (!this.customer || !this.customer.name) return "";
      return this.customer.name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },
    completedTasks() {
      return this.tasks.filter((task) => !!task.completed_at).length;
    },
    overdueTasks() {
      const today = moment().startOf("day");
      return this.tasks.filter(
        (task) =>
          !task.completed_at &&
          task.due_date &&
          moment(task.due_date).startOf("day").isBefore(today)
      ).length;
    },
    overallProgress() {
      if (!this.tasks.length) return 0;
      return Math.round((this.completedTasks / this.tasks.length) * 100);
    },
    statusLabel() {
      if (!this.tasks.length) return "Nenhuma tarefa cadastrada";
      if (this.overallProgress === 100) return "Plano concluído";
      if (this.overdueTasks) {
        return `${this.overdueTasks} ${
          this.overdueTasks === 1 ? "tarefa atrasada" : "tarefas atrasadas"
        }`;
      }
      return "Plano em andamento";
    },
    ringCircumference() {
      return 2 * Math.PI * RING_RADIUS;
    },
    ringOffset() {
      return this.ringCircumference * (1 - this.overallProgress / 100);
    },
    stats() {
      return [
        {
          key: "milestones",
          label: "Marcos",
          value: this.milestones.length,
          icon: "FlagIcon",
          variant: "primary",
        },
        {
          key: "tasks",
          label: "Tarefas concluídas",
          value: `${this.completedTasks}/${this.tasks.length}`,
          icon: "CheckCircleIcon",
          variant: "success",
        },
        {
          key: "overdue",
          label: "Tarefas atrasadas",
          value: this.overdueTasks,
          icon: "AlertCircleIcon",
          variant: this.overdueTasks ? "danger" : "secondary",
        },
        {
          key: "goals",
          label: "Objetivos secundários",
          value: this.secondaryGoals.length,
          icon: "TargetIcon",
          variant: "info",
        },
      ];
    },
  },
  mounted() {
    this.loading = true;
    this.getCustomer(this.$route.params.id)
      .then((response) => {
        if (response && response.data) {
          this.customer = response.data;
        } else {
          this.notifyError();
        }
      })
      .catch(() => {
        this.notifyError();
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    ...mapActions({
      getCustomer: sharedTypes.GET_CUSTOMER,
    }),
    goBack() {
      this.$router.back();
    },
    notifyError() {
      this.toast({
        component: ToastificationContent,
        props: {
          title: "Oops!",
          text: "Ocorreu um erro ao obter as informações. Entre em contato com o setor de TI.",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.plan-hero {
  position: relative;
  overflow: hidden;

  &__bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      118deg,
      rgba(115, 103, 240, 0.12) 0%,
      rgba(115, 103, 240, 0.02) 55%,
      rgba(0, 207, 232, 0.1) 100%
    );
  }

  &__body {
    position: relative;
  }

  &__avatar {
    font-weight: 600;
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.25);
  }

  &__eyebrow {
    font-size: 0.75rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7367f0;
    font-weight: 600;
  }

  &__title {
    font-weight: 600;
    line-height: 1.2;
  }

  &__meta {
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    color: #6e6b7b;
    margin-top: 2px;
  }
}

.plan-progress {
  position: relative;
  width: 76px;
  height: 76px;

  &__ring {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }

  &__track,
  &__value {
    fill: none;
    stroke-width: 3.5;
    stroke-linecap: round;
  }

  &__track {
    stroke: rgba(115, 103, 240, 0.15);
  }

  &__value {
    stroke: #7367f0;
    transition: stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &__label {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;

    strong {
      font-size: 1rem;
    }

    small {
      font-size: 0.6rem;
      color: #6e6b7b;
    }
  }
}

.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 22px rgba(34, 41, 47, 0.12);
  }

  &__value {
    font-weight: 600;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    flex-shrink: 0;

    &--primary {
      background-color: rgba(115, 103, 240, 0.12);
      color: #7367f0;
    }
    &--success {
      background-color: rgba(40, 199, 111, 0.12);
      color: #28c76f;
    }
    &--danger {
      background-color: rgba(234, 84, 85, 0.12);
      color: #ea5455;
    }
    &--info {
      background-color: rgba(0, 207, 232, 0.12);
      color: #00cfe8;
    }
    &--secondary {
      background-color: rgba(130, 134, 139, 0.12);
      color: #82868b;
    }
  }
}

.plan-tabs {
  ::v-deep .plan-tabs__nav {
    padding: 0.75rem 1rem 0;
    border-bottom: 1px solid rgba(34, 41, 47, 0.08);

    .nav-link {
      display: flex;
      align-items: center;
      font-weight: 500;
    }
  }

  ::v-deep .plan-tabs__content {
    padding: 1.25rem;
  }
}

@media (max-width: 575.98px) {
  .plan-hero__actions {
    width: 100%;
    justify-content: space-between;
    margin-top: 1rem;
  }
}
</style>
